import type { ReactNode } from 'react';
import { DsButton, DsPill } from '@/modules/internal-v2/ds';
import { SampleItems } from './sample-items.component';
import type { ReviewSampleItem } from '@/api/specifications/specifications.types';

interface ProposalRowProps {
  /** Stable key for selection state. */
  selectionKey: string;
  /** Optional crumb (e.g. "Apple · smartphone"). */
  crumb?: ReactNode;
  name: ReactNode;
  state: 'proposed' | 'approved';
  itemCount: number;
  sampleItems: ReviewSampleItem[];
  canApprove: boolean;
  canManage: boolean;
  /** Hides the checkbox (for sub-rows that aren't bulk-selectable yet). */
  selectable?: boolean;
  selected: boolean;
  onSelectChange: (next: boolean) => void;
  onApprove?: () => void;
  onMerge?: () => void;
  onReject?: () => void;
  /** Slot for row-type-specific extras (e.g. "Approve branch", conflict chip). */
  extra?: ReactNode;
  /** True if this row is nested (model under product line). */
  child?: boolean;
}

function badgeClass(n: number): string {
  if (n > 100) return 'sp-count high';
  if (n >= 10) return 'sp-count med';
  return 'sp-count';
}

export function ProposalRow({
  crumb,
  name,
  state,
  itemCount,
  sampleItems,
  canApprove,
  canManage,
  selectable = true,
  selected,
  onSelectChange,
  onApprove,
  onMerge,
  onReject,
  extra,
  child,
}: ProposalRowProps) {
  const rejectDisabled = itemCount > 0;
  const rejectTitle = rejectDisabled
    ? `${itemCount} Artikel zugeordnet — bitte mergen statt löschen`
    : 'Vorschlag ablehnen (löschen)';

  // Approved rows don't need the bulk checkbox.
  const showCheckbox = selectable && canManage && state === 'proposed';

  return (
    <div className={`sp-row${child ? ' is-child' : ''}${selected ? ' is-selected' : ''}`}>
      <div className="sp-row-check">
        {showCheckbox && (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelectChange(e.target.checked)}
            aria-label="Für Sammelaktionen auswählen"
          />
        )}
      </div>

      <div className="sp-row-main">
        <div className="sp-row-title">
          <DsPill status={state === 'approved' ? 'pos' : 'warn'}>
            {state === 'approved' ? 'approved' : 'proposed'}
          </DsPill>
          {crumb && <span className="sp-crumb">{crumb}</span>}
          <span className="sp-name">{name}</span>
          <span className={badgeClass(itemCount)} title={`${itemCount} Artikel`}>
            {itemCount}
          </span>
          {extra}
        </div>
        <SampleItems items={sampleItems} totalCount={itemCount} />
      </div>

      {canManage && (
        <div className="sp-row-actions">
          {canApprove && onApprove && (
            <DsButton variant="primary" onClick={onApprove}>
              Freigeben
            </DsButton>
          )}
          {onMerge && (
            <DsButton variant="ghost" onClick={onMerge}>
              Mergen…
            </DsButton>
          )}
          {onReject && (
            <DsButton
              variant="ghost"
              onClick={onReject}
              disabled={rejectDisabled}
              title={rejectTitle}
            >
              Ablehnen
            </DsButton>
          )}
        </div>
      )}
    </div>
  );
}
